import type { CrmDeal } from '@integrations/crm-deals/contracts/crm-deal.contract';

import { inject } from '@angular/core';
import type { CreateQueryOptions } from '@tanstack/angular-query-experimental';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { retryTransient } from '@integrations/shared/retry-policy';

import { CrmDealsApiService } from '../api/crm-deals-api.service';

export const CRM_DEALS_QUERY_KEY = ['crm-deals'] as const;

type CrmDealsQueryOverrides = Partial<Omit<CreateQueryOptions<CrmDeal[]>, 'queryKey' | 'queryFn'>>;

/**
 * Deal attributes are fetched per pipeline — the query key carries the
 * pipeline id, so each pipeline gets its own cache entry (the NgRx store used
 * a keyed collection-query adapter for the same reason). The query stays
 * disabled while `pipelineIdFn()` is falsy, regardless of `enabled` overrides.
 */
export function injectCrmDealsQuery(
    pipelineIdFn: () => number | null | undefined,
    optionsFn?: () => CrmDealsQueryOverrides
) {
    const api = inject(CrmDealsApiService);

    return injectQuery(() => {
        const pipelineId = pipelineIdFn() ?? null;
        const { enabled = true, ...overrides } = optionsFn?.() ?? {};

        return {
            staleTime: 5 * 60 * 1000, // 5 minutes
            // A definitive 4xx is how "the account does not have this" is detected, so it
            // must not be retried; a transient failure gets a few attempts instead of being
            // mistaken for that answer (#71534).
            retry: retryTransient,
            queryKey: [...CRM_DEALS_QUERY_KEY, pipelineId] as const,
            queryFn: () => lastValueFrom(api.getDealsAttributes(pipelineId)),
            ...overrides,
            enabled: Boolean(pipelineId) && Boolean(enabled),
        };
    });
}
